import React, { useState, useEffect } from 'react';
import { 
  X, Search, Save, Plus, Edit2, Trash2, RotateCcw, 
  DollarSign, User, Calculator, TrendingUp, CreditCard, Download
} from 'lucide-react';
import '../styles/CustomerInvoice.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const SupplierPayments = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Search state
  const [paymentSearch, setPaymentSearch] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Payment list
  const [payments, setPayments] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  
  // Payment form state
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentForm, setPaymentForm] = useState(getEmptyPaymentForm());
  
  // Payment allocations (linking payment to invoices)
  const [allocations, setAllocations] = useState([]);
  const [editingAllocationIndex, setEditingAllocationIndex] = useState(null);
  const [allocationForm, setAllocationForm] = useState(getEmptyAllocationForm());
  
  // Dropdown data
  const [suppliers, setSuppliers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  function getEmptyPaymentForm() {
    return {
      PaymentNo: '',
      PaymentDate: new Date().toISOString().slice(0, 10),
      SupplierID: '',
      PaymentModeID: '',
      BankAccountID: '',
      Amount: 0,
      ReferenceNo: '',
      ChequeNo: '',
      ChequeDate: '',
      Notes: '',
      Status: 'Paid'
    };
  }
  
  function getEmptyAllocationForm() {
    return {
      InvoiceID: '',
      InvoiceNo: '',
      OurReferenceNo: '',
      InvoiceAmount: 0,
      BalanceAmount: 0,
      AllocatedAmount: 0
    };
  }
  
  useEffect(() => {
    loadDropdownData();
    generatePaymentNumber();
    loadPayments();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (onClose) {
          onClose();
        } else {
          navigate('/dashboard');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, navigate]);

  useEffect(() => {
    if (paymentForm.SupplierID) {
      loadUnpaidInvoices(paymentForm.SupplierID);
    } else {
      setUnpaidInvoices([]);
    }
  }, [paymentForm.SupplierID]);
  
  const loadDropdownData = async () => {
    try {
      const [suppRes, pmRes, baRes] = await Promise.all([
        apiClient.get('/Suppliers.php'),
        apiClient.get('/PaymentModes.php'),
        apiClient.get('/BankAccounts.php')
      ]);
      
      if (suppRes.data.success) setSuppliers(suppRes.data.data || []);
      if (pmRes.data.success) setPaymentModes(pmRes.data.data || []);
      if (baRes.data.success) setBankAccounts(baRes.data.data || []);
      
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Error loading dropdown data');
    }
  };
  
  const loadUnpaidInvoices = async (supplierId) => {
    try {
      const response = await apiClient.get(`/SupplierPayments.php?unpaid-invoices=${supplierId}`);
      if (response.data.success) {
        setUnpaidInvoices(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading unpaid invoices:', err);
    }
  };
  
  const generatePaymentNumber = async () => {
    try {
      const response = await apiClient.get('/SupplierPayments.php/next-number');
      if (response.data.success) {
        setPaymentForm(prev => ({ 
          ...prev, 
          PaymentNo: response.data.data.paymentNo 
        }));
      }
    } catch (err) {
      console.error('Error generating payment number:', err);
      const timestamp = Date.now();
      setPaymentForm(prev => ({ 
        ...prev, 
        PaymentNo: `PAY-${timestamp}` 
      }));
    }
  };
  
  const loadPayments = async () => {
    try {
      const response = await apiClient.get('/SupplierPayments.php');
      if (response.data.success) {
        setPayments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    }
  };
  
  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({ ...prev, [name]: value }));
  };
  
  const handleAllocationFormChange = (e) => {
    const { name, value } = e.target;
    setAllocationForm(prev => {
      const updated = { ...prev, [name]: value };
      
      if (name === 'InvoiceID') {
        const selectedInvoice = unpaidInvoices.find(inv => inv.SupplierInvoiceID === value);
        if (selectedInvoice) {
          updated.InvoiceNo = selectedInvoice.InvoiceNo;
          updated.OurReferenceNo = selectedInvoice.OurReferenceNo;
          updated.InvoiceAmount = parseFloat(selectedInvoice.TotalAmount || 0);
          updated.BalanceAmount = parseFloat(selectedInvoice.BalanceAmount || 0);
          updated.AllocatedAmount = updated.BalanceAmount;
        }
      }
      
      return updated;
    });
  };
  
  const handleAddAllocation = () => {
    if (!allocationForm.InvoiceID) {
      setError('Please select an invoice');
      return;
    }
    
    if (parseFloat(allocationForm.AllocatedAmount) <= 0) {
      setError('Allocated amount must be greater than 0');
      return;
    }
    
    if (parseFloat(allocationForm.AllocatedAmount) > parseFloat(allocationForm.BalanceAmount)) {
      setError('Allocated amount cannot exceed invoice balance');
      return;
    }
    
    // Check if invoice already allocated
    const existingIndex = allocations.findIndex(
      (a, idx) => a.InvoiceID === allocationForm.InvoiceID && idx !== editingAllocationIndex
    );
    
    if (existingIndex !== -1) {
      setError('This invoice is already allocated');
      return;
    }
    
    if (editingAllocationIndex !== null) {
      const updatedAllocations = [...allocations];
      updatedAllocations[editingAllocationIndex] = { ...allocationForm };
      setAllocations(updatedAllocations);
      setEditingAllocationIndex(null);
    } else {
      setAllocations([...allocations, { ...allocationForm }]);
    }
    
    setAllocationForm(getEmptyAllocationForm());
    calculateTotalAllocated([...allocations, allocationForm]);
  };
  
  const handleEditAllocation = (index) => {
    setAllocationForm(allocations[index]);
    setEditingAllocationIndex(index);
  };
  
  const handleDeleteAllocation = (index) => {
    const updatedAllocations = allocations.filter((_, i) => i !== index);
    setAllocations(updatedAllocations);
    calculateTotalAllocated(updatedAllocations);
  };
  
  const handleCancelAllocationEdit = () => {
    setAllocationForm(getEmptyAllocationForm());
    setEditingAllocationIndex(null);
  };
  
  const calculateTotalAllocated = (allocs) => {
    const total = allocs.reduce((sum, alloc) => sum + parseFloat(alloc.AllocatedAmount || 0), 0);
    setPaymentForm(prev => ({ ...prev, Amount: total.toFixed(2) }));
  };
  
  const handleSavePayment = async () => {
    if (!paymentForm.SupplierID) {
      setError('Please select a supplier');
      return;
    }
    
    if (!paymentForm.PaymentModeID) {
      setError('Please select a payment mode');
      return;
    }
    
    if (parseFloat(paymentForm.Amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const dataToSave = {
        ...paymentForm,
        Allocations: allocations
      };
      
      let response;
      if (editingPaymentId) {
        dataToSave.PaymentID = editingPaymentId;
        response = await apiClient.put('/SupplierPayments.php', dataToSave);
      } else {
        response = await apiClient.post('/SupplierPayments.php', dataToSave);
      }
      
      if (response.data.success) {
        setSuccess(editingPaymentId ? 'Payment updated successfully!' : 'Payment created successfully!');
        handleClearAll();
        loadPayments();
        generatePaymentNumber();
      } else {
        setError(response.data.message || 'Failed to save payment');
      }
    } catch (err) {
      console.error('Error saving payment:', err);
      setError('Error saving payment: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearchPayment = async () => {
    if (!paymentSearch.trim()) {
      setError('Please enter payment number');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get(`/SupplierPayments.php?paymentno=${paymentSearch}`);
      
      if (response.data.success && response.data.data) {
        loadPaymentForEdit(response.data.data);
      } else {
        setError('Payment not found');
      }
    } catch (err) {
      console.error('Error searching payment:', err);
      setError('Error searching payment: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const loadPaymentForEdit = (payment) => {
    setEditingPaymentId(payment.PaymentID);
    setPaymentForm({
      PaymentNo: payment.PaymentNo,
      PaymentDate: payment.PaymentDate,
      SupplierID: payment.SupplierID,
      PaymentModeID: payment.PaymentModeID,
      BankAccountID: payment.BankAccountID || '',
      Amount: payment.Amount,
      ReferenceNo: payment.ReferenceNo || '',
      ChequeNo: payment.ChequeNo || '',
      ChequeDate: payment.ChequeDate || '',
      Notes: payment.Notes || '',
      Status: payment.Status
    });
    setAllocations(payment.Allocations || []);
    setSelectedPayment(payment);
  };
  
  const handleClearAll = () => {
    setPaymentForm(getEmptyPaymentForm());
    setAllocations([]);
    setAllocationForm(getEmptyAllocationForm());
    setEditingPaymentId(null);
    setEditingAllocationIndex(null);
    setSelectedPayment(null);
    setPaymentSearch('');
    setError('');
    setSuccess('');
    generatePaymentNumber();
  };
  
  if (!session?.companyid) {
    return (
      <div 
        className="invoice-form-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            if (onClose) {
              onClose();
            } else {
              navigate('/dashboard');
            }
          }
        }}
      >
        <div className="invoice-form-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button 
              className="close-btn-fixed" 
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  navigate('/dashboard');
                }
              }}
              type="button"
              title="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="invoice-form-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (onClose) {
            onClose();
          } else {
            navigate('/dashboard');
          }
        }
      }}
    >
      <div className="invoice-form-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="form-header">
          <div className="header-left">
            <CreditCard size={24} />
            <h2>Supplier Payment (Payment Made)</h2>
          </div>
          <button 
            className="close-btn-fixed"
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate('/dashboard');
              }
            }}
            type="button"
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="invoice-toolbar">
          <button onClick={handleSearchPayment} disabled={loading} className="btn-toolbar">
            <Search size={18} />
            Search
          </button>
          <button 
            onClick={handleSavePayment} 
            disabled={loading || parseFloat(paymentForm.Amount) <= 0} 
            className="btn-toolbar btn-save"
          >
            <Save size={18} />
            {editingPaymentId ? 'Update' : 'Save'}
          </button>
          <button onClick={handleClearAll} disabled={loading} className="btn-toolbar">
            <RotateCcw size={18} />
            Clear All
          </button>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <strong>Success:</strong> {success}
          </div>
        )}
        
        {/* Search Section */}
        <div className="invoice-search-section">
          <label>Payment No:</label>
          <input
            type="text"
            value={paymentSearch}
            onChange={(e) => setPaymentSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchPayment()}
            placeholder="Enter payment number to search"
            disabled={loading}
          />
          <button onClick={handleSearchPayment} disabled={loading} className="btn-search-inline">
            <Search size={18} />
          </button>
        </div>
        
        <div className="form-content">
          {/* Payment Header */}
          <div className="form-section">
            <h3 className="section-title">
              <DollarSign size={18} />
              Payment Details
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Payment No *</label>
                <input
                  type="text"
                  name="PaymentNo"
                  value={paymentForm.PaymentNo}
                  onChange={handlePaymentFormChange}
                  readOnly
                  style={{ background: '#e3f2fd', fontWeight: 'bold' }}
                />
              </div>
              
              <div className="form-group">
                <label>Payment Date *</label>
                <input
                  type="date"
                  name="PaymentDate"
                  value={paymentForm.PaymentDate}
                  onChange={handlePaymentFormChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Supplier *</label>
                <select
                  name="SupplierID"
                  value={paymentForm.SupplierID}
                  onChange={handlePaymentFormChange}
                  required
                >
                  <option value="">Select Supplier</option>
                  {suppliers.map(s => (
                    <option key={s.supplierid} value={s.supplierid}>
                      {s.suppliername} ({s.suppliercode})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Payment Mode *</label>
                <select
                  name="PaymentModeID"
                  value={paymentForm.PaymentModeID}
                  onChange={handlePaymentFormChange}
                  required
                >
                  <option value="">Select Payment Mode</option>
                  {paymentModes.map(pm => (
                    <option key={pm.paymentmodeid} value={pm.paymentmodeid}>
                      {pm.PaymentMode}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Bank Account</label>
                <select
                  name="BankAccountID"
                  value={paymentForm.BankAccountID}
                  onChange={handlePaymentFormChange}
                >
                  <option value="">Select Bank Account</option>
                  {bankAccounts.map(ba => (
                    <option key={ba.bankaccountid} value={ba.bankaccountid}>
                      {ba.BankName} - {ba.AccountNumber}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  name="Amount"
                  value={paymentForm.Amount}
                  onChange={handlePaymentFormChange}
                  placeholder="0.00"
                  style={{ background: '#ffe6e6', fontWeight: 'bold', fontSize: '16px' }}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Reference No</label>
                <input
                  type="text"
                  name="ReferenceNo"
                  value={paymentForm.ReferenceNo}
                  onChange={handlePaymentFormChange}
                  placeholder="Transaction reference"
                />
              </div>
              
              <div className="form-group">
                <label>Cheque No</label>
                <input
                  type="text"
                  name="ChequeNo"
                  value={paymentForm.ChequeNo}
                  onChange={handlePaymentFormChange}
                  placeholder="If payment by cheque"
                />
              </div>
              
              <div className="form-group">
                <label>Cheque Date</label>
                <input
                  type="date"
                  name="ChequeDate"
                  value={paymentForm.ChequeDate}
                  onChange={handlePaymentFormChange}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="Status"
                  value={paymentForm.Status}
                  onChange={handlePaymentFormChange}
                >
                  <option value="Paid">Paid</option>
                  <option value="Cleared">Cleared</option>
                  <option value="Bounced">Bounced</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Invoice Allocations */}
          {paymentForm.SupplierID && (
            <div className="form-section">
              <h3 className="section-title">
                <Calculator size={18} />
                Allocate to Invoices (Optional)
              </h3>
              
              {/* Add/Edit Allocation Form */}
              <div className="item-form">
                <div className="form-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                  <div className="form-group">
                    <label>Invoice</label>
                    <select
                      name="InvoiceID"
                      value={allocationForm.InvoiceID}
                      onChange={handleAllocationFormChange}
                    >
                      <option value="">Select Invoice</option>
                      {unpaidInvoices.map(inv => (
                        <option key={inv.SupplierInvoiceID} value={inv.SupplierInvoiceID}>
                          {inv.OurReferenceNo} - Balance: {parseFloat(inv.BalanceAmount).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Invoice Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={allocationForm.InvoiceAmount}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Balance Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={allocationForm.BalanceAmount}
                      readOnly
                      className="readonly-field"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Allocated Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="AllocatedAmount"
                      value={allocationForm.AllocatedAmount}
                      onChange={handleAllocationFormChange}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button onClick={handleAddAllocation} className="btn-primary btn-sm" style={{ width: '100%' }}>
                      {editingAllocationIndex !== null ? (
                        <>
                          <Save size={16} />
                          Update
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          Add
                        </>
                      )}
                    </button>
                    {editingAllocationIndex !== null && (
                      <button onClick={handleCancelAllocationEdit} className="btn-secondary btn-sm" style={{ marginLeft: '8px' }}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Allocations List */}
              {allocations.length > 0 && (
                <div className="invoice-items-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Our Reference</th>
                        <th>Supplier Invoice</th>
                        <th className="text-right">Invoice Amount</th>
                        <th className="text-right">Balance</th>
                        <th className="text-right">Allocated</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocations.map((alloc, index) => (
                        <tr key={index}>
                          <td>{alloc.OurReferenceNo}</td>
                          <td>{alloc.InvoiceNo}</td>
                          <td className="text-right">{parseFloat(alloc.InvoiceAmount).toFixed(2)}</td>
                          <td className="text-right">{parseFloat(alloc.BalanceAmount).toFixed(2)}</td>
                          <td className="text-right font-weight-bold">
                            {parseFloat(alloc.AllocatedAmount).toFixed(2)}
                          </td>
                          <td className="actions-cell">
                            <button
                              onClick={() => handleEditAllocation(index)}
                              className="btn-icon btn-edit"
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteAllocation(index)}
                              className="btn-icon btn-delete"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ background: '#f9f9f9', fontWeight: 'bold' }}>
                        <td colSpan="4" style={{ textAlign: 'right', paddingRight: '12px' }}>
                          Total Allocated:
                        </td>
                        <td className="text-right" style={{ color: '#667eea', fontSize: '16px' }}>
                          {allocations.reduce((sum, a) => sum + parseFloat(a.AllocatedAmount || 0), 0).toFixed(2)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {/* Notes */}
          <div className="form-section">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Notes</label>
                <textarea
                  name="Notes"
                  value={paymentForm.Notes}
                  onChange={handlePaymentFormChange}
                  rows="3"
                  placeholder="Additional notes about this payment"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierPayments;